import { Trans, useLingui } from '@lingui/react/macro';

import { BaseMoneyCell } from '@/components/ArticlesTableCells';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useBrandRequestContext } from '@/layouts/Brand/BrandRequestContext';
import { isActionMandatory } from '@/models/article';
import { ArticleActionWithRelations, ArticleWithRelations } from '@/models/request';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { AcceptRefuseServiceRadioButtons } from './components/AcceptRefuseServiceRadioButtons';
import { ActionDefects } from './components/ActionDefects';
import { ActionName } from './components/ActionName';
import { ArticlePriceCardFooter, ArticlePriceRow } from './components/ArticlePrice';
import { ClientExchangeCard, ClientExchangeDetails } from './components/ClientExchangeDetails';
import { NotedDefectsCards, NotedDefectsTable } from './components/NotedDefects';
import { OtherServiceCard, OtherServiceTable } from './components/OtherService';

import './ClientArticleServices.css';

export const ClientArticleServices = ({
  article,
  showPrice,
}: {
  article: ArticleWithRelations;
  showPrice: boolean;
}) => {
  const { isMobile } = useViewPort();
  const { request } = useBrandRequestContext();

  const exchangedFromArticle = request?.allArticles.find((a) => a.id === article.exchangedFromId);

  if (exchangedFromArticle) {
    return isMobile ? (
      <ClientExchangeCard article={exchangedFromArticle} variant="confirmed" />
    ) : (
      <ClientExchangeDetails article={exchangedFromArticle} variant="confirmed" />
    );
  }

  const showAcceptRefuseService =
    article.step?.step === 'validation' &&
    !!article.step?.config.actionLevelValidation?.enabled &&
    !article.quoteRefusedAt &&
    !article.validationCompletedAt;

  if (isMobile) {
    return (
      <ClientArticleServicesList
        article={article}
        showPrice={showPrice}
        showAcceptRefuseService={showAcceptRefuseService}
      />
    );
  }

  return (
    <ClientArticleServicesTable
      article={article}
      showPrice={showPrice}
      showAcceptRefuseService={showAcceptRefuseService}
    />
  );
};

const { block: clientArticleServicesTableBlock, element: clientArticleServicesTableElement } =
  createBEMClasses('client-article-services-table');

const ClientArticleServicesTable = ({
  article,
  showPrice,
  showAcceptRefuseService = false,
}: {
  article: ArticleWithRelations;
  showPrice: boolean;
  showAcceptRefuseService?: boolean;
}) => {
  const { t } = useLingui();

  if (!article.snapshot.serviceChoice) {
    return <NotedDefectsTable article={article} />;
  }

  if (article.snapshot.serviceChoice === 'exchange') {
    return <ClientExchangeDetails article={article} variant="proposal" />;
  }

  if (article.snapshot.serviceChoice !== 'care-repair') {
    return <OtherServiceTable article={article} />;
  }

  const showDefectsColumn = !!article.snapshot.articleDefects.length;
  const columnWidths = Object.values({
    serviceRequired: 'minmax(200px, 1fr)',
    associatedDefects: showDefectsColumn && 'minmax(200px, 1fr)',
    price: showPrice && '140px',
    acceptRefuseService: showAcceptRefuseService && '140px',
  });

  return (
    <Table
      theme="brand"
      ariaLabel={t({ id: 'client.article.services', message: 'Services' })}
      columnWidths={columnWidths}
      bordered
      className={clientArticleServicesTableBlock()}
    >
      <Header>
        <Row>
          <Column>
            <Trans id="client.article.services.column.service">Service required</Trans>
          </Column>
          {showDefectsColumn && (
            <Column>
              <Trans id="client.article.services.column.defect">Associated defects</Trans>
            </Column>
          )}
          {showPrice && (
            <Column justifyContent="flex-end">
              <Trans id="client.article.services.column.price">Price (incl. VAT)</Trans>
            </Column>
          )}
          {showAcceptRefuseService && <Column justifyContent="flex-end" />}
        </Row>
      </Header>

      <Body>
        {article.step?.step === 'validation' && !!article.previousSnapshot
          ? article.previousSnapshot.articleActions.map((action) => {
              const actionOnCurrentSnapshot = article.snapshot.articleActions.find(
                ({ stableId }) => stableId === action.stableId
              );

              return (
                <ActionRow
                  key={action.id}
                  action={actionOnCurrentSnapshot ?? action}
                  isPreviousAction={!actionOnCurrentSnapshot}
                  article={article}
                  showDefectsColumn={showDefectsColumn}
                  showPricesColumn={showPrice}
                  showAcceptRefuseService={showAcceptRefuseService}
                />
              );
            })
          : article.snapshot.articleActions.map((action) => (
              <ActionRow
                key={action.id}
                action={action}
                article={article}
                showDefectsColumn={showDefectsColumn}
                showPricesColumn={showPrice}
              />
            ))}
        {showPrice && <ArticlePriceRow article={article} showDefectsColumn={showDefectsColumn} />}
      </Body>
    </Table>
  );
};

const ActionRow = ({
  action,
  isPreviousAction = false,
  article,
  showDefectsColumn,
  showPricesColumn,
  showAcceptRefuseService = false,
}: {
  action: ArticleActionWithRelations;
  isPreviousAction?: boolean;
  article: ArticleWithRelations;
  showDefectsColumn: boolean;
  showPricesColumn: boolean;
  showAcceptRefuseService?: boolean;
}) => {
  const hasParentActions = action.parents.length;
  const isMandatoryAction = isActionMandatory(action);
  const showAcceptRefuseServiceRadioButtons = !hasParentActions && !isMandatoryAction;

  return (
    <Row className={clientArticleServicesTableElement('row', { deleted: isPreviousAction })}>
      <Cell className={clientArticleServicesTableElement('action-name')}>
        <ActionName
          action={action}
          isDisabled={isPreviousAction || !!article.quoteRefusedAt}
          article={article}
        />
      </Cell>
      {showDefectsColumn && (
        <Cell className={clientArticleServicesTableElement('action-defects')}>
          <ActionDefects
            action={action}
            isDisabled={isPreviousAction || !!article.quoteRefusedAt}
            isPreviousAction={isPreviousAction}
            article={article}
          />
        </Cell>
      )}
      {showPricesColumn && (
        <Cell justifyContent="flex-end" className={clientArticleServicesTableElement('price')}>
          <BaseMoneyCell
            price={action.organizationPrice?.amountPerCurrency?.[0]}
            showAmountBeforeTaxes={false}
            isDisabled={isPreviousAction || !!article.quoteRefusedAt}
          />
        </Cell>
      )}
      {showAcceptRefuseService && (
        <Cell justifyContent="flex-end">
          {showAcceptRefuseServiceRadioButtons && (
            <AcceptRefuseServiceRadioButtons
              action={action}
              isPreviousAction={isPreviousAction}
              article={article}
            />
          )}
        </Cell>
      )}
    </Row>
  );
};

const { block, element } = createBEMClasses('client-article-services-list');

const ClientArticleServicesList = ({
  article,
  showPrice,
  showAcceptRefuseService = false,
}: {
  article: ArticleWithRelations;
  showPrice: boolean;
  showAcceptRefuseService?: boolean;
}) => {
  if (!article.snapshot.serviceChoice) {
    return <NotedDefectsCards article={article} />;
  }

  if (article.snapshot.serviceChoice === 'exchange') {
    return <ClientExchangeCard article={article} variant="proposal" />;
  }

  if (article.snapshot.serviceChoice !== 'care-repair') {
    return <OtherServiceCard article={article} />;
  }

  return (
    <Box padding="0" className={block()}>
      {article.step?.step === 'validation' && !!article.previousSnapshot
        ? article.previousSnapshot.articleActions.map((action) => {
            const actionOnCurrentSnapshot = article.snapshot.articleActions.find(
              ({ stableId }) => stableId === action.stableId
            );

            return (
              <ActionCard
                key={action.id}
                action={actionOnCurrentSnapshot ?? action}
                article={article}
                showPrice={showPrice}
                showAcceptRefuseService={showAcceptRefuseService}
                isPreviousAction={!actionOnCurrentSnapshot}
              />
            );
          })
        : article.snapshot.articleActions.map((action) => (
            <ActionCard
              key={action.id}
              action={action}
              article={article}
              showPrice={showPrice}
              showAcceptRefuseService={showAcceptRefuseService}
            />
          ))}
      {showPrice && <ArticlePriceCardFooter article={article} />}
    </Box>
  );
};

const ActionCard = ({
  action,
  article,
  showPrice,
  showAcceptRefuseService = false,
  isPreviousAction = false,
}: {
  action: ArticleActionWithRelations;
  article: ArticleWithRelations;
  showPrice: boolean;
  showAcceptRefuseService?: boolean;
  isPreviousAction?: boolean;
}) => {
  const hasParentActions = action.parents.length;
  const isMandatoryAction = isActionMandatory(action);

  const showAcceptRefuseServiceRadioButtons = !hasParentActions && !isMandatoryAction;

  return (
    <Stack padding="12px" gap="0.5rem" className={element('card', { deleted: isPreviousAction })}>
      <Stack
        row
        gap="1rem"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="nowrap"
      >
        <div className={element('action-name')}>
          <ActionName
            isDisabled={isPreviousAction || !!article.quoteRefusedAt}
            action={action}
            article={article}
          />
        </div>

        {showPrice && (
          <BaseMoneyCell
            isDisabled={isPreviousAction || !!article.quoteRefusedAt}
            price={action.organizationPrice?.amountPerCurrency?.[0]}
            showAmountBeforeTaxes={false}
          />
        )}
      </Stack>
      <div className={element('action-defects')}>
        <ActionDefects
          isDisabled={isPreviousAction || !!article.quoteRefusedAt}
          isPreviousAction={isPreviousAction}
          action={action}
          article={article}
        />
      </div>
      {showAcceptRefuseService && showAcceptRefuseServiceRadioButtons && (
        <AcceptRefuseServiceRadioButtons
          action={action}
          isPreviousAction={isPreviousAction}
          article={article}
        />
      )}
    </Stack>
  );
};
